import { z } from 'zod';

/**
 * Schemas Zod des paramètres tenant (Phase 6).
 *
 * Toutes les sections vivent dans `tenants.parametres` (JSONB unique).
 * Chaque section est typée et validée ; les sections absentes prennent
 * les défauts définis dans `DEFAULT_TENANT_SETTINGS`.
 *
 *  - `branding`      : couleurs + favicon + welcome message (US-ADM-01 + US-TR-06)
 *  - `general`       : adresse + coordonnées + contact (US-ADM-01)
 *  - `localization`  : fuseau + devise + langue + formats date/heure (US-ADM-01)
 *  - `security`      : politique mdp + sessions + verrouillage (US-ADM-05)
 *  - `email`         : config Resend tenant-level (US-ADM-06)
 *  - `numbering`     : préfixes + réinitialisation (US-ADM-09)
 *  - `workflows`     : toggles ON/OFF des workflows optionnels (US-ADM-09)
 *  - `constants`     : taux horaire technicien, devise héritée (US-ADM-09)
 *  - `notifications` : délais alertes échéances (US-ADM-09)
 */

// ─── Branding (US-ADM-01 + US-TR-06) ────────────────────────────────────────

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Format hex attendu : #RRGGBB');

export const brandingSchema = z.object({
  primary_color: hexColorSchema.default('#0d9488'),
  secondary_color: hexColorSchema.default('#0891b2'),
  accent_color: hexColorSchema.default('#7c3aed'),
  // Peut être une URL externe (CDN) OU une clé S3/MinIO (tenant-id/branding/favicon.webp).
  // Le frontend résout via `favicon_signed_url` exposé par GET /admin/tenant/settings.
  favicon_url: z.string().max(500).nullable().optional(),
  welcome_message: z.string().max(500).optional().default(''),
});
export type BrandingSettings = z.infer<typeof brandingSchema>;

// ─── General (US-ADM-01) ────────────────────────────────────────────────────

const contactPrincipalInnerSchema = z.object({
  nom: z.string().max(255).optional().default(''),
  prenom: z.string().max(255).optional().default(''),
  email: z.string().email().or(z.literal('')).optional().default(''),
  telephone: z.string().max(50).optional().default(''),
});

export const generalSettingsSchema = z.object({
  adresse: z.string().max(500).optional().default(''),
  telephone: z.string().max(50).optional().default(''),
  email_general: z.string().email().or(z.literal('')).optional().default(''),
  site_web: z.string().url().or(z.literal('')).optional().default(''),
  contact_principal: contactPrincipalInnerSchema.default(() =>
    contactPrincipalInnerSchema.parse({}),
  ),
});
export type GeneralSettings = z.infer<typeof generalSettingsSchema>;

// ─── Localization (US-ADM-01) ───────────────────────────────────────────────

export const localizationSettingsSchema = z.object({
  timezone: z.string().default('Europe/Paris'),
  currency: z.string().length(3).default('EUR'),
  language: z.enum(['fr', 'en']).default('fr'),
  date_format: z.enum(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']).default('DD/MM/YYYY'),
  time_format: z.enum(['24h', '12h']).default('24h'),
});
export type LocalizationSettings = z.infer<typeof localizationSettingsSchema>;

// ─── Security (US-ADM-05) ───────────────────────────────────────────────────

const passwordPolicyInnerSchema = z.object({
  min_length: z.coerce.number().int().min(6).max(128).default(8),
  require_uppercase: z.boolean().default(true),
  require_lowercase: z.boolean().default(true),
  require_digit: z.boolean().default(true),
  require_special: z.boolean().default(true),
  /** Nombre des N derniers mots de passe interdits. 0 = pas d'historique. */
  history_count: z.coerce.number().int().min(0).max(50).default(5),
  /** Durée de validité (jours). 0 = jamais d'expiration. */
  max_age_days: z.coerce.number().int().min(0).max(3650).default(0),
});

const sessionPolicyInnerSchema = z.object({
  /** Durée d'inactivité avant expiration (minutes). */
  idle_timeout_minutes: z.coerce.number().int().min(5).max(43200).default(480),
  /** Durée maximale d'une session (heures). */
  max_session_hours: z.coerce.number().int().min(1).max(720).default(24),
});

const lockoutPolicyInnerSchema = z.object({
  max_failed_attempts: z.coerce.number().int().min(3).max(20).default(5),
  lockout_duration_minutes: z.coerce.number().int().min(1).max(1440).default(15),
  notify_admins_on_lockout: z.boolean().default(true),
});

const uploadPolicyInnerSchema = z.object({
  /**
   * Taille maximale (Mo) d'un fichier uploadé tous endpoints confondus
   * (avatar, logo, documents techniques, custom_fields type=file). Lue par
   * le plugin Fastify multipart au boot ET enforcée à la validation métier.
   * Cap absolu plateforme : 100 Mo (au-delà → reverse proxy KO).
   */
  max_file_size_mb: z.coerce.number().int().min(1).max(100).default(10),
  /**
   * Extensions autorisées (liste blanche). Vide = pas de restriction par
   * extension (le contrôle se fait sur le MIME côté endpoint).
   */
  allowed_extensions: z.array(z.string().regex(/^[a-z0-9]{1,10}$/i)).default([]),
});

export const securitySettingsSchema = z.object({
  password_policy: passwordPolicyInnerSchema.default(() => passwordPolicyInnerSchema.parse({})),
  session_policy: sessionPolicyInnerSchema.default(() => sessionPolicyInnerSchema.parse({})),
  lockout_policy: lockoutPolicyInnerSchema.default(() => lockoutPolicyInnerSchema.parse({})),
  upload_policy: uploadPolicyInnerSchema.default(() => uploadPolicyInnerSchema.parse({})),
});
export type SecuritySettings = z.infer<typeof securitySettingsSchema>;

// ─── Email (US-ADM-06) ──────────────────────────────────────────────────────

export const emailSettingsSchema = z.object({
  sender_name: z.string().max(255).optional().default(''),
  sender_email: z.string().email().or(z.literal('')).optional().default(''),
  reply_to: z.string().email().or(z.literal('')).optional().default(''),
  notification_email: z.string().email().or(z.literal('')).optional().default(''),
  /** Domaine envoyeur — doit être vérifié auprès de Resend (DKIM/SPF). */
  sender_domain: z.string().max(255).optional().default(''),
  /** Statut de vérification du domaine. */
  domain_status: z
    .enum(['non_configure', 'en_attente_validation', 'valide'])
    .default('non_configure'),
});
export type EmailSettings = z.infer<typeof emailSettingsSchema>;

// ─── Numbering (US-ADM-09) ──────────────────────────────────────────────────

export const numberingSettingsSchema = z.object({
  equipment_prefix: z.string().max(20).default('EQ-'),
  intervention_prefix: z.string().max(20).default('INT-'),
  reservation_prefix: z.string().max(20).default('RES-'),
  /** Préfixes ajoutés en Phase 8/9 — consommés par les modules bâtiments/services. */
  batiment_prefix: z.string().max(20).default('BAT-'),
  service_prefix: z.string().max(20).default('SVC-'),
  salle_prefix: z.string().max(20).default('SALLE-'),
  /** 'continuous' = incrément global / 'yearly' = reset chaque année. */
  reset_strategy: z.enum(['continuous', 'yearly']).default('continuous'),
});
export type NumberingSettings = z.infer<typeof numberingSettingsSchema>;

// ─── Workflows (US-ADM-09) ──────────────────────────────────────────────────

export const workflowSettingsSchema = z.object({
  require_intervention_validation: z.boolean().default(true),
  require_reservation_validation: z.boolean().default(true),
  enable_multilevel_approval: z.boolean().default(false),
});
export type WorkflowSettings = z.infer<typeof workflowSettingsSchema>;

// ─── Constants (US-ADM-09) ──────────────────────────────────────────────────

export const constantSettingsSchema = z.object({
  technician_hourly_rate: z.coerce.number().min(0).default(50),
});
export type ConstantSettings = z.infer<typeof constantSettingsSchema>;

// ─── Notification delays (US-ADM-09) ────────────────────────────────────────

export const notificationDelaysSchema = z.object({
  maintenance_alert_days: z.array(z.coerce.number().int().min(0)).default([30, 7, 1]),
  calibration_alert_days: z.array(z.coerce.number().int().min(0)).default([60, 30, 7]),
  warranty_alert_days: z.array(z.coerce.number().int().min(0)).default([60, 30]),
});
export type NotificationDelays = z.infer<typeof notificationDelaysSchema>;

// ─── Aggregate ──────────────────────────────────────────────────────────────

export const tenantSettingsSchema = z.object({
  branding: brandingSchema.optional(),
  general: generalSettingsSchema.optional(),
  localization: localizationSettingsSchema.optional(),
  security: securitySettingsSchema.optional(),
  email: emailSettingsSchema.optional(),
  numbering: numberingSettingsSchema.optional(),
  workflows: workflowSettingsSchema.optional(),
  constants: constantSettingsSchema.optional(),
  notification_delays: notificationDelaysSchema.optional(),
});
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

/**
 * Défauts complets (toutes les sections au complet) — utilisé quand un
 * tenant n'a JAMAIS rien sauvegardé.
 */
export const DEFAULT_TENANT_SETTINGS: Required<{
  [K in keyof TenantSettings]: NonNullable<TenantSettings[K]>;
}> = {
  branding: brandingSchema.parse({}),
  general: generalSettingsSchema.parse({}),
  localization: localizationSettingsSchema.parse({}),
  security: securitySettingsSchema.parse({}),
  email: emailSettingsSchema.parse({}),
  numbering: numberingSettingsSchema.parse({}),
  workflows: workflowSettingsSchema.parse({}),
  constants: constantSettingsSchema.parse({}),
  notification_delays: notificationDelaysSchema.parse({}),
};

/**
 * Merge un objet partiel `parametres` (DB) avec les défauts. Section absente
 * → défaut complet. Champs manquants dans une section → défaut du champ.
 */
export function mergeTenantSettings(stored: unknown): Required<{
  [K in keyof TenantSettings]: NonNullable<TenantSettings[K]>;
}> {
  const parsed = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {};
  return {
    branding: brandingSchema.parse(parsed.branding ?? {}),
    general: generalSettingsSchema.parse(parsed.general ?? {}),
    localization: localizationSettingsSchema.parse(parsed.localization ?? {}),
    security: securitySettingsSchema.parse(parsed.security ?? {}),
    email: emailSettingsSchema.parse(parsed.email ?? {}),
    numbering: numberingSettingsSchema.parse(parsed.numbering ?? {}),
    workflows: workflowSettingsSchema.parse(parsed.workflows ?? {}),
    constants: constantSettingsSchema.parse(parsed.constants ?? {}),
    notification_delays: notificationDelaysSchema.parse(parsed.notification_delays ?? {}),
  };
}

// ─── Email templates (US-ADM-07) ────────────────────────────────────────────

export const EMAIL_TEMPLATE_CATEGORIES = ['transactionnel', 'notification', 'marketing'] as const;
export type EmailTemplateCategory = (typeof EMAIL_TEMPLATE_CATEGORIES)[number];

export const createEmailTemplateSchema = z.object({
  code: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z][a-z0-9_]*$/, 'snake_case minuscule'),
  nom: z.string().min(2).max(255),
  description: z.string().max(2000).optional().nullable(),
  categorie: z.enum(EMAIL_TEMPLATE_CATEGORIES).default('transactionnel'),
  sujet: z.string().min(1).max(500),
  contenu_html: z.string().min(1),
  is_active: z.boolean().default(true),
});
export type CreateEmailTemplateInput = z.infer<typeof createEmailTemplateSchema>;

export const updateEmailTemplateSchema = createEmailTemplateSchema.partial();
export type UpdateEmailTemplateInput = z.infer<typeof updateEmailTemplateSchema>;

export const previewEmailTemplateSchema = z.object({
  sujet: z.string().min(1).max(500),
  contenu_html: z.string().min(1),
  /** Variables fictives pour la prévisualisation. */
  variables: z.record(z.string(), z.unknown()).default({}),
});
export type PreviewEmailTemplateInput = z.infer<typeof previewEmailTemplateSchema>;

export const sendTestEmailSchema = z.object({
  to: z.string().email(),
  sujet: z.string().min(1).max(500).optional(),
  contenu_html: z.string().min(1).optional(),
  template_code: z.string().min(1).max(80).optional(),
  variables: z.record(z.string(), z.unknown()).default({}),
});
export type SendTestEmailInput = z.infer<typeof sendTestEmailSchema>;
